import {NoteLinkedObjectType, NotePartType} from 'notebook/util/NoteUtilConstants';
import notifications, {NotificationOptions, NotificationType} from 'notifications';

export const DEFAULT_CONTENT_SUMMARY = 'New content';

// Keep in sync with benchling/notebook/document_update_summary.py
export enum DocumentUpdateType {
  INSERTING = 'inserting',
  APPENDING = 'appending',
  INSERTED = 'inserted',
  APPENDED = 'appended',
}

export type DocumentContentType = NoteLinkedObjectType | NotePartType;

export interface DocumentUpdateNotification {
  updateType: DocumentUpdateType;
  contentTypes: Array<DocumentContentType>;
  notificationType: NotificationType;
}

const CONTENT_TYPE_GLOSSARY: Partial<{[contentType in DocumentContentType]: string}> = {
  [NoteLinkedObjectType.NOTE_LINKED_CHART]: 'A chart',
  [NotePartType.TEXT]: 'Text content',
};

const UPDATE_MESSAGE_GLOSSARY: {[updateType in DocumentUpdateType]: string} = {
  [DocumentUpdateType.INSERTING]: 'is being inserted into the document.',
  [DocumentUpdateType.APPENDING]: 'is being appended to the document.',
  [DocumentUpdateType.INSERTED]: 'has been inserted into the document.',
  [DocumentUpdateType.APPENDED]: 'has been appended to the document.',
};

const NOTIFICATION_FUNCTIONS: {
  [notificationType in NotificationType]: () => (message: string, options?: NotificationOptions) => void;
} = {
  [NotificationType.INFO]: () => notifications.info,
  [NotificationType.SUCCESS]: () => notifications.success,
  [NotificationType.WARNING]: () => notifications.warn,
  [NotificationType.DANGER]: () => notifications.error,
};

function describe(contentType: DocumentContentType): string {
  return CONTENT_TYPE_GLOSSARY[contentType] || DEFAULT_CONTENT_SUMMARY;
}

function summarize(updateType: DocumentUpdateType, contentTypes: Array<DocumentContentType>): string {
  const contentSummary = contentTypes.length === 1 ? describe(contentTypes[0]) : DEFAULT_CONTENT_SUMMARY;
  const updateSummary = UPDATE_MESSAGE_GLOSSARY[updateType];
  return `${contentSummary} ${updateSummary}`;
}

/**
 * Builds a stable toast id for a document's update notifications. Passing the same id for the
 * "in progress" (gray) and "completed" (green) toasts lets the completed toast replace the in-progress
 * toast in place, rather than stacking a second toast alongside it.
 */
export const getDocumentUpdateToastId = (fileId: string): string => `document-update-notification-${fileId}`;

export const displayNotification = (
  {updateType, contentTypes, notificationType}: DocumentUpdateNotification,
  toastId?: string
): void => {
  const notify = NOTIFICATION_FUNCTIONS[notificationType]();
  const message = summarize(updateType, contentTypes);
  // Only pass options when we have a real toast id. Passing {id: undefined} would override the
  // default generated id in the legacy notifications path, causing notifications to clobber each other.
  if (toastId != null) {
    notify(message, {id: toastId});
  } else {
    notify(message);
  }
};
